import React from 'react';
import messages from '../text/messages';
import { isTrue, normalizeEIN, normalizeNIN, normalizeSSN, normalizeUTR } from './string';
import selectOptions from '../text/selectOptions';

import { Field } from 'redux-form';
import { normalizeABA, required, validABA } from './validation';
import { SelectField, TextField } from '../components/fields';
import { IC_DOCUMENTS, IC_DOCUMENTS_UK } from '../components/document/types';
import { DocumentInfo } from './documentTypes';

import WelcomeBackStep from '../pages/onboarding/tenants/contractor/steps/contractor/welcomeBack';
import UploadPromisedDocuments from '../pages/onboarding/tenants/contractor/steps/contractor/uploadPromisedDocuments';
import ContractorOfferLetter from '../pages/onboarding/tenants/contractor/steps/contractor/contractorOfferLetter';
import ContractorAssignmentLetter from '../pages/onboarding/tenants/contractor/steps/contractor/contractorAssignmentLetter';
import ContractorArbitrationStep from '../pages/onboarding/tenants/contractor/steps/contractor/contractorArbitration';
import ContractorDirectDepositStep from '../pages/onboarding/tenants/contractor/steps/contractor/contractorDirectDeposit';
import TaxDocumentsStep from '../pages/onboarding/tenants/contractor/steps/contractor/taxDocuments';
import ContractorNextStepsStep from '../pages/onboarding/tenants/contractor/steps/contractor/contractorNextSteps';

import VendorInformationStep from '../pages/onboarding/tenants/contractor/steps/contractor/vendorInformation';
import ClassificationStep from '../pages/onboarding/tenants/contractor/steps/contractor/classification';
import InsuranceRequirementsStep from '../pages/onboarding/tenants/contractor/steps/contractor/insuranceRequirements';
import DocumentUploadStep from '../pages/onboarding/tenants/contractor/steps/contractor/documentUpload';

import EmployeeDirectDepositStep from '../pages/onboarding/tenants/contractor/steps/employee/employeeDirectDeposit';
import W4Step from '../pages/onboarding/tenants/contractor/steps/employee/w4Step';
import InsuranceAndBenefitsStep from '../pages/onboarding/tenants/contractor/steps/employee/insuranceAndBenefits';
import I9RegistrationStep from '../pages/onboarding/tenants/contractor/steps/employee/i9Registration';
import I9DocumentsStep from '../pages/onboarding/tenants/contractor/steps/employee/i9Documents';
import EmployeeNextStepsStep from '../pages/onboarding/tenants/contractor/steps/employee/employeeNextSteps';
import RightToWorkStep from '../pages/onboarding/tenants/contractor/steps/employee/rightToWork';
import PensionContributionStep from '../pages/onboarding/tenants/contractor/steps/employee/pensionContribution';
import NewStarterInfo from '../pages/onboarding/tenants/contractor/steps/employee/newStarterInfo';
import { sectionHead } from '../pages/worker/tabs/_common';
import { FormattedMessage } from 'react-intl';

export function ssnFieldLabel(regime) {
    switch ((regime || {}).onboarding_flow_id) {
        case 'UK':
            return messages.nationalInsuranceNumber;
        default:
            return messages.socialSecurityNumber;
    }
}

export function ssnFieldNormalize(regime) {
    switch ((regime || {}).onboarding_flow_id) {
        case 'UK':
            return normalizeNIN;
        default:
            return normalizeSSN;
    }
}

export function mailingAddressFieldLabel(regime) {
    switch ((regime || {}).onboarding_flow_id) {
        case 'UK':
            return messages.postalAddress;
        default:
            return messages.mailingAddress;
    }
}

export function businessTypeFieldOptions(regime) {
    switch ((regime || {}).onboarding_flow_id) {
        case 'UK':
            return selectOptions.companyTypesUK;
        default:
            return selectOptions.companyTypes;
    }
}

export function taxIdFieldLabel(businessType) {
    switch (businessType) {
        case 'sole_prop':
        case 'individual':
            return messages.socialSecurityNumber;
        case 'selfemployed':
            return messages.companyUTR;
        case 'LC':
            return messages.companyRegistrationNumber;
        default:
            return messages.companyTaxId;
    }
}

export function taxIdFieldNormalize(businessType) {
    switch (businessType) {
        case 'sole_prop':
        case 'individual':
            return normalizeSSN;
        case 'selfemployed':
            return normalizeNIN;
        case 'LC':
            return normalizeUTR;
        default:
            return normalizeEIN;
    }
}

export function taxType(businessType) {
    switch (businessType) {
        case 'sole_prop':
        case 'individual':
            return 'SSN';
        case 'selfemployed':
            return 'UTR';
        case 'LC':
            return 'Company Number';
        default:
            return 'EIN';
    }
}

export function taxFields(regime, businessType) {
    switch ((regime || {}).onboarding_flow_id) {
        case 'UK':
            return (
                <React.Fragment>
                    <div className="col-xs-12">
                        <Field
                            name="company.business_type"
                            validate={required}
                            component={SelectField}
                            options={selectOptions.companyTypesUK}
                            placeholder={messages.typeOfBusiness}
                        />
                    </div>
                    <div className="col-md-6 col-sm-7 col-xs-12">
                        <Field
                            name="company.tax_id"
                            validate={required}
                            normalize={taxIdFieldNormalize(businessType || selectOptions.companyTypesUK[0].value)}
                            component={TextField}
                            placeholder={taxIdFieldLabel(businessType || selectOptions.companyTypesUK[0].value)}
                        />
                    </div>
                    <div className="col-md-6 col-sm-5 col-xs-12">
                        <Field name="company.VATIN" component={TextField} placeholder={messages.companyVATIN} />
                    </div>
                </React.Fragment>
            );
        default:
            return (
                <React.Fragment>
                    <div className="colbusinessType-sm-7 col-xs-12">
                        <Field
                            name="company.business_type"
                            validate={required}
                            component={SelectField}
                            options={selectOptions.companyTypes}
                            placeholder={messages.typeOfBusiness}
                        />
                    </div>
                    <div className="col-sm-5 col-xs-12">
                        <Field
                            name="company.tax_id"
                            validate={required}
                            normalize={taxIdFieldNormalize(businessType)}
                            component={TextField}
                            placeholder={taxIdFieldLabel(businessType)}
                        />
                    </div>
                </React.Fragment>
            );
    }
}

export function contractorTypeLabel(regime) {
    switch ((regime || {}).onboarding_flow_id) {
        case 'UK':
            return 'You will paid by {admin} under PAYE.';
        default:
            return 'You will be paid as a W-2 employee of {admin}.';
    }
}

export function contractorType1099Label(regime) {
    switch ((regime || {}).onboarding_flow_id) {
        case 'UK':
            return 'You must be self-employed or have your own qualifying Limited Company.';
        default:
            return 'You will be paid as a 1099 independent contractor.';
    }
}

export function jobDescriptionIC(regime) {
    switch ((regime || {}).onboarding_flow_id) {
        case 'UK':
            return `<h4>Contract as a business</h4>
                                <h5>Benefits</h5>
                                <ul>
                                    <li>We will provide easy time-tracking and automated invoicing.</li>
                                    <li>We will pay your business entity.</li>
                                    <li>You will get paid accurately and on time.</li>
                                </ul>
                                <h5>Requirements</h5>
                                <ul>
                                    <li>You must demonstrate you are an independent business.</li>
                                    <li>You will be responsible for your own taxes, including National Insurance.</li>
                                </ul>
                                <h5>Documents</h5>
                                <ul>
                                    <li>Certificate of Incorporation<span class="required">Required if incorporated</span></li>
                                    <li>Other Documentation of Business Income<span class="recommended">Recommended</span></li>
                                    <li>Proof of Investment<span class="recommended">Recommended</span></li>
                                    <li>Marketing materials<span class="recommended">Recommended</span></li>
                                   
                                </ul>`;
        default:
            return `<h4>Contract as a business</h4>
                                <h5>Benefits</h5>
                                <ul>
                                    <li>We will provide easy time-tracking and automated invoicing.</li>
                                    <li>We will issue a 1099 at year end.</li>
                                    <li>You will get paid accurately and on time.</li>
                                </ul>
                                <h5>Requirements</h5>
                                <ul>
                                    <li>You must demonstrate you are an independent business.</li>
                                    <li>You will be responsible for your own taxes, including payroll or self-employment taxes.</li>
                                    <li>You will need to have business insurance before starting the assignment.</li>
                                </ul>
                                <h5>Documents</h5>
                                <ul>
                                    <li>Proof of insurance<i> or </i>willingness to obtain<span class="required">Required</span></li>
                                    <li>Certificate of Incorporation<span class="required">Required if incorporated</span></li>
                                    <li>Certificate of Good Standing<span class="required">Required if incorporated</span></li>
                                    <li>Proof of other income sources<span class="recommended">Recommended</span></li>
                                    <li>Marketing materials<span class="recommended">Recommended</span></li>
                                </ul>`;
    }
}

export function jobDescriptionW2(regime) {
    switch ((regime || {}).onboarding_flow_id) {
        case 'UK':
            return `<h4>Contract as an individual</h4>
                            <h5>Benefits</h5>
                            <ul>
                                <li>We will provide a dedicated HR support specialist.</li>
                                <li>We will manage all tax witholdings.</li>
                                <li>You will get paid accurately and on time.</li>
                            </ul>
                            <h5>Requirements</h5>
                            <ul>
                                <li>You are required to track your hours via our dashboard.</li>
                                <li>You may be required to complete a background check.</li>
                            </ul>`;
        default:
            return `<h4>Contract as an individual</h4>
                            <h5>Benefits</h5>
                            <ul>
                                <li>We will provide a dedicated HR support specialist.</li>
                                <li>We will give you access to corporate-style employee benefits.</li>
                                <li>We will manage all tax witholdings.</li>
                                <li>We will issue you a W-2 at year end.</li>
                                <li>You will get paid accurately and on time.</li>
                            </ul>
                            <h5>Requirements</h5>
                            <ul>
                                <li>You are required to track your hours via our dashboard.</li>
                                <li>You may be required to complete a background check.</li>
                            </ul>`;
    }
}

export function icSteps(moveTo, rest) {
    switch ((rest.regime || {}).onboarding_flow_id) {
        case 'UK':
            return [
                <VendorInformationStep onSubmit={moveTo(1)} message={messages.contractorCompanyInfo} {...rest} />,
                <DocumentUploadStep message={messages.uploadAndFinishIC} {...rest} />
            ];
        default:
            return [
                <VendorInformationStep onSubmit={moveTo(1)} message={messages.contractorCompanyInfo} {...rest} />,
                <ClassificationStep onSubmit={moveTo(2)} message={messages.contractorICClassification} {...rest} />,
                <InsuranceRequirementsStep onSubmit={moveTo(3)} message={messages.insuranceRequirements} {...rest} />,
                <DocumentUploadStep message={messages.uploadAndFinishIC} {...rest} />
            ];
    }
}

export function icStepsContinuation(moveTo, rest) {
    switch ((rest.regime || {}).onboarding_flow_id) {
        case 'UK':
            return [
                <WelcomeBackStep onSubmit={moveTo(1)} message={messages.welcomeBack} {...rest} />,
                <UploadPromisedDocuments onSubmit={moveTo(2)} message={messages.promisedDocumentUpload} {...rest} />,
                <ContractorOfferLetter onSubmit={moveTo(3)} message={messages.contractorAgreement} {...rest} />,
                <ContractorAssignmentLetter onSubmit={moveTo(4)} message={messages.assignmentLetter} {...rest} />,
                <ContractorArbitrationStep onSubmit={moveTo(5)} message={messages.signatureAndArbitration} {...rest} />,
                <ContractorDirectDepositStep onSubmit={moveTo(6)} message={messages.directDeposit} {...rest} />,
                <ContractorNextStepsStep message={messages.congratulations} {...rest} />
            ];
        default:
            return [
                <WelcomeBackStep onSubmit={moveTo(1)} message={messages.welcomeBack} {...rest} />,
                <UploadPromisedDocuments onSubmit={moveTo(2)} message={messages.promisedDocumentUpload} {...rest} />,
                <ContractorOfferLetter onSubmit={moveTo(3)} message={messages.contractorAgreement} {...rest} />,
                <ContractorAssignmentLetter onSubmit={moveTo(4)} message={messages.assignmentLetter} {...rest} />,
                <ContractorArbitrationStep onSubmit={moveTo(5)} message={messages.signatureAndArbitration} {...rest} />,
                <ContractorDirectDepositStep onSubmit={moveTo(6)} message={messages.directDeposit} {...rest} />,
                <TaxDocumentsStep onSubmit={moveTo(7)} message={messages.taxDocuments} {...rest} />,
                <ContractorNextStepsStep message={messages.congratulations} {...rest} />
            ];
    }
}

export function BGSteps(regime, bgSteps) {
    switch ((regime || {}).onboarding_flow_id) {
        case 'UK':
            return [];
        default:
            return bgSteps;
    }
}

export function afterBGSteps(moveTo, rest) {
    switch ((rest.regime || {}).onboarding_flow_id) {
        case 'UK':
            return [
                <EmployeeDirectDepositStep onSubmit={moveTo(1)} message={messages.directDeposit} {...rest} />,
                <NewStarterInfo onSubmit={moveTo(2)} message={messages.newStaterInfo} {...rest} />,
                <PensionContributionStep onSubmit={moveTo(3)} message={messages.pensionContribution} {...rest} />,
                <RightToWorkStep onSubmit={moveTo(4)} message={messages.rightToWork} {...rest} />,
                <EmployeeNextStepsStep message={messages.congratulations} {...rest} />
            ];
        default:
            return [
                <EmployeeDirectDepositStep onSubmit={moveTo(1)} message={messages.directDeposit} {...rest} />,
                <W4Step onSubmit={moveTo(2)} message={messages.w4} {...rest} />,
                <InsuranceAndBenefitsStep onSubmit={moveTo(3)} message={messages.insuranceAndBenefits} {...rest} />,
                <I9RegistrationStep onSubmit={moveTo(4)} message={messages.i9Registration} {...rest} />,
                <I9DocumentsStep onSubmit={moveTo(5)} message={messages.i9Registration2} {...rest} hidden />,
                <EmployeeNextStepsStep message={messages.congratulations} {...rest} />
            ];
    }
}

export function icDocuments(regime, renderDocumentRow) {
    switch ((regime || {}).onboarding_flow_id) {
        case 'UK': {
            const { incorporation, marketing, investment, incomeSources } = IC_DOCUMENTS_UK;

            return [
                <div className="col-xs-12" key={'icDocuments'}>
                    {renderDocumentRow(incorporation)}
                    {renderDocumentRow(incomeSources)}
                    {renderDocumentRow(marketing)}
                    {renderDocumentRow(investment)}
                </div>
            ];
        }
        default: {
            const { goodStanding, incorporation, dba, form1099, marketing, investment, incomeSources } = IC_DOCUMENTS;

            return [
                <div className="col-xs-12" key={'icDocuments'}>
                    {renderDocumentRow(incorporation)}
                    {renderDocumentRow(dba)}
                    {renderDocumentRow(goodStanding)}
                    {renderDocumentRow(form1099)}
                    {renderDocumentRow(incomeSources)}
                    {renderDocumentRow(marketing)}
                    {renderDocumentRow(investment)}
                </div>
            ];
        }
    }
}

export function icDocumentsList(regime) {
    switch ((regime || {}).onboarding_flow_id) {
        case 'UK': {
            return IC_DOCUMENTS_UK;
        }
        default: {
            return IC_DOCUMENTS;
        }
    }
}

export function employeeHandBook(regime) {
    switch ((regime || {}).onboarding_flow_id) {
        case 'UK': {
            return DocumentInfo.types.ADMIN_EMPLOYEE_HANDBOOK_UK;
        }
        default: {
            return DocumentInfo.types.ADMIN_EMPLOYEE_HANDBOOK;
        }
    }
}
export function bankingCheckPreferenceField(regime) {
    return !['UK'].includes((regime || {}).onboarding_flow_id);
}

export function bankRoutingFieldLabel(regime) {
    switch ((regime || {}).onboarding_flow_id) {
        case 'UK':
            return messages.sortCode;
        default:
            return messages.bankRouting;
    }
}

export function bankRoutingFieldNormalize(regime) {
    switch ((regime || {}).onboarding_flow_id) {
        case 'UK':
            return undefined;
        default:
            return normalizeABA;
    }
}

export function bankRoutingFieldValidate(regime) {
    switch ((regime || {}).onboarding_flow_id) {
        case 'UK':
            return () => undefined;
        default:
            return validABA;
    }
}

export function checkingAccountFieldLabel(regime) {
    switch ((regime || {}).onboarding_flow_id) {
        case 'UK':
            return messages.ukCheckingAccount;
        default:
            return messages.checkingAccount;
    }
}

export function w4TableRows(regime, user) {
    switch ((regime || {}).onboarding_flow_id) {
        case 'UK':
            return [
                sectionHead(messages.newStaterInfo),
                {
                    label: <FormattedMessage {...messages.employeeStatement} />,
                    value: getEmployeeStatement(user)
                },
                { label: <FormattedMessage {...messages.taxCode} />, value: user.w4_fields ? user.w4_fields.tax_code || '-' : '-' },
                {
                    label: <FormattedMessage {...messages.studentLoan} />,
                    value: (
                        <div>
                            {getLoanAnswer(user, 'studentLoanPlan')}
                            {getLoanAnswer(user, 'studiesCompletion')}
                            {getLoanAnswer(user, 'creditCardRepaying')}
                            {getLoanAnswer(user, 'studentLoanType')}
                        </div>
                    )
                },
                {
                    label: <FormattedMessage {...messages.postgraduateLoan} />,
                    value: (
                        <div>
                            {getLoanAnswer(user, 'postgraduateLoan')}
                            {getLoanAnswer(user, 'postgraduateCompletion')}
                            {getLoanAnswer(user, 'postgraduateCreditCardRepaying')}
                        </div>
                    )
                }
            ];
        default:
            return [
                sectionHead(messages.w4),
                {
                    label: <FormattedMessage {...messages.w4FillingStatus} />,
                    value: getFilingStatus(user)
                },
                { label: <FormattedMessage {...messages.w4TotalAllowances} />, value: user.w4_fields ? user.w4_fields.totalAllowances : '-' },
                {
                    label: <FormattedMessage {...messages.w4AdditionalWithholding} />,
                    value: user.w4_fields ? user.w4_fields.additionalWithholding : '-'
                }
            ];
    }
}

function getLoanAnswer(user, question) {
    if (!user || !user.w4_fields) return undefined;
    const { w4_fields } = user;
    const options = selectOptions.newStarterInfoQuestions;

    switch (question) {
        case 'studentLoanPlan':
            return (
                <div>
                    <span>
                        {options.studentLoanPlan.title}: &nbsp;
                        <b>{isTrue(w4_fields && w4_fields.studentLoanPlan && w4_fields.studentLoanPlan.value) ? 'Yes' : 'No'}</b>
                    </span>
                </div>
            );
        case 'studiesCompletion': {
            return (
                isTrue((w4_fields.studentLoanPlan || {}).value) && (
                    <div>
                        <span>
                            {options.studiesCompletion.title}: &nbsp;
                            <b>{isTrue(w4_fields && w4_fields.studiesCompletion && w4_fields.studiesCompletion.value) ? 'Yes' : 'No'}</b>
                        </span>
                    </div>
                )
            );
        }
        case 'creditCardRepaying': {
            return (
                isTrue((w4_fields.studentLoanPlan || {}).value) &&
                isTrue((w4_fields.studiesCompletion || {}).value) && (
                    <div>
                        <span>
                            {options.creditCardRepaying.title}: &nbsp;
                            <b>{isTrue(w4_fields && w4_fields.creditCardRepaying && w4_fields.creditCardRepaying.value) ? 'Yes' : 'No'}</b>
                        </span>
                    </div>
                )
            );
        }
        case 'studentLoanType':
            return (
                isTrue((w4_fields.studentLoanPlan || {}).value) &&
                isTrue((w4_fields.studiesCompletion || {}).value) &&
                (w4_fields.creditCardRepaying || {}).value === 'false' && (
                    <div>
                        <span>
                            {options.studentLoanType.title}: &nbsp;
                            <b>{w4_fields && w4_fields.studentLoanType && w4_fields.studentLoanType.value}</b>
                        </span>
                    </div>
                )
            );

        case 'postgraduateLoan':
            return (
                <div>
                    <span>
                        {options.postgraduateLoan.title}: &nbsp;
                        <b>{isTrue(w4_fields && w4_fields.postgraduateLoan && w4_fields.postgraduateLoan.value) ? 'Yes' : 'No'}</b>
                    </span>
                </div>
            );
        case 'postgraduateCompletion':
            return (
                isTrue((w4_fields.postgraduateLoan || {}).value) && (
                    <div>
                        <span>
                            {options.postgraduateCompletion.title}: &nbsp;
                            <b>{isTrue(w4_fields && w4_fields.postgraduateCompletion && w4_fields.postgraduateCompletion.value) ? 'Yes' : 'No'}</b>
                        </span>
                    </div>
                )
            );
        case 'postgraduateCreditCardRepaying':
            return (
                isTrue((w4_fields.postgraduateLoan || {}).value) &&
                isTrue((w4_fields.postgraduateCompletion || {}).value) && (
                    <div>
                        <span>
                            {options.postgraduateCreditCardRepaying.title}: &nbsp;
                            <b>
                                {isTrue(w4_fields && w4_fields.postgraduateCreditCardRepaying && w4_fields.postgraduateCreditCardRepaying.value) ? 'Yes' : 'No'}
                            </b>
                        </span>
                    </div>
                )
            );
        default:
            return undefined;
    }
}

function getFilingStatus(user) {
    if (!user || !user.w4_fields) return '-';

    const option = selectOptions.filingStatus.find(o => o.value === user.w4_fields.filingStatus);
    if (!option) return '-';

    return option.label;
}

function getEmployeeStatement(user) {
    if (!user || !user.w4_fields) return '-';
    const statements = {
        first: <FormattedMessage {...messages.employeeStatementFirst} />,
        other: <FormattedMessage {...messages.employeeStatementOther} />,
        another: <FormattedMessage {...messages.employeeStatementAnother} />
    };
    return statements[user.w4_fields.employee_statement] || '-';
}



// WEBPACK FOOTER //
// ./src/utilities/regime.jsx